import { Fragment, useEffect, useState } from "react";
import { bool, func, number, object, shape, string } from "prop-types";
import styled from "styled-components/macro";
import { rem, wordWrap } from "polished";

import { BREAKPOINT_WRAPPER, routes, URL_ORIGIN } from "../../constants";
import { colors, fonts } from "../../styles/Styles.variables";
import { formatTel } from "../../helpers/format";

import { Helmet } from "react-helmet";

import Alert from "../../styles/components/Alert";
import FilestackImage from "../filestack-image";
import LocationDirections from "./components/Location.directions";
import LocationHero from "./components/Location.hero";
import LocationRecentListings from "./components/Location.recent-listings";
import LocationTeamMembers from "./components/Location.team-members";
import LocationTestimonial from "./components/Location.testimonial";
import MapEmbed from "../map-embed";
import Meta from "../meta";
import Raw from "../../styles/components/Raw";

const locationTitle = "HOLT Used Retail Center - ";

const Location = ({
  locationsCount,
  locationsError,
  locationsFetch,
  locationsLoading,
  locationsLookup,
  history,
  match,
  prerenderReady,
  targetDevice,
  trackOutboundLink,
  ...rest
}) => {
  const [isRedirect, setIsRedirect] = useState(false);
  const [location, setLocation] = useState(null);
  const [query, setQuery] = useState(null);
  const { params, url } = match;

  useEffect(() => {
    if (!locationsCount && !locationsError && !locationsLoading) {
      locationsFetch();
    }
  }, [locationsCount, locationsError, locationsFetch, locationsLoading]);

  useEffect(() => {
    if (
      !locationsError &&
      !locationsLoading &&
      (!location || (!!location && location.slug !== params.slug))
    ) {
      locationsLookup(params.slug)
        .then(response => {
          if (response.location.slug !== params.slug) {
            history.replace(
              `${routes.location.path}/${response.location.slug}`
            );
            setIsRedirect(true);
          }

          setLocation(response.location);
          const query = `?locationZip=${response.location.locationZip}&distance=30&limit=6`;
          setQuery(query);

          if (!!prerenderReady) {
            prerenderReady();
          }
        })
        .catch(error => {
          history.push({
            pathname: routes.notFound.path,
            state: { notFound: url },
          });
        });
    }
  }, [
    history,
    location,
    locationsError,
    locationsLoading,
    locationsLookup,
    params,
    prerenderReady,
    url,
  ]);

  return (
    <Fragment>
      <Meta
        title={`${locationTitle} ${
          (!!location && location.locationCity) || ""
        }`}
      />
      {isRedirect && (
        <Helmet defer={false}>
          <meta name="prerender-status-code" content="301" />
          <meta
            name="prerender-header"
            content={`Location: ${URL_ORIGIN}${routes.location.path}/${location.slug}`}
          />
        </Helmet>
      )}
      {!locationsLoading &&
        !locationsError &&
        !!location &&
        !!location.hero && (
          <LocationHero
            alt={`${locationTitle} ${location.locationCity}`}
            handle={location.hero}
            targetDevice={targetDevice}
          />
        )}
      <Article>
        {locationsLoading && <Alert>Loading…</Alert>}
        {!locationsLoading && !!locationsError && (
          <Alert type="error">{locationsError}</Alert>
        )}
        {!locationsLoading && !locationsError && !!location && (
          <Fragment>
            <Content>
              <h1>
                {locationTitle}
                {location.locationCity}
              </h1>
              <Row>
                <Secondary>
                  <h2>Address</h2>
                  <p>
                    {location.locationAddress}
                    {location.locationAddress2 && (
                      <>
                        <br />
                        {location.locationAddress2}
                      </>
                    )}
                    <br />
                    {location.locationCity}, {location.locationState}{" "}
                    {location.locationZip}
                  </p>
                  <h3>Phone</h3>
                  <p className="md-offset">
                    <a
                      className="subtle"
                      href={`tel:1-${formatTel(location.phone)}`}
                      onClick={e => {
                        if (!!trackOutboundLink) {
                          trackOutboundLink(e, {
                            action: `${locationTitle}${location.locationCity} Phone Number`,
                            label: "location",
                          });
                        }
                      }}
                    >
                      {location.phone}
                    </a>
                  </p>
                  <p>
                    <LocationDirections
                      location={location}
                      locationTitle={locationTitle}
                      onClick={e => {
                        if (!!trackOutboundLink) {
                          trackOutboundLink(e, {
                            action: `${locationTitle}${location.locationCity} Directions`,
                            label: "location",
                          });
                        }
                      }}
                    />
                  </p>
                </Secondary>
                <Primary>
                  <LocationMap>
                    <MapEmbed
                      address={location.locationAddress}
                      city={location.locationCity}
                      name={`${locationTitle}${location.locationCity}`}
                      placeId={location.placeId}
                      state={location.locationState}
                      zip={location.locationZip}
                    />
                  </LocationMap>
                </Primary>
              </Row>
              {!!location.bannerMobile && targetDevice === "mobile" && (
                <Banner
                  alt={location.bannerMobileAlt || "banner"}
                  handle={location.bannerMobile}
                  height={533}
                  theme="dark"
                  width={840}
                />
              )}
              {!!location.bannerDesktop && targetDevice !== "mobile" && (
                <Banner
                  alt={location.bannerDesktopAlt || "banner"}
                  handle={location.bannerDesktop}
                  height={320}
                  width={1160}
                />
              )}
              {!!location.testimonialQuote && (
                <LocationTestimonial
                  quote={location.testimonialQuote}
                  source={location.testimonialSource}
                />
              )}
              {!!location.team && !!location.team.length && (
                <LocationTeamMembers
                  targetDevice={targetDevice}
                  team={location.team}
                />
              )}
              <Description html={location.description} />
            </Content>
            {!!query && (
              <LocationRecentListings
                {...rest}
                location={location}
                query={query}
                targetDevice={targetDevice}
              />
            )}
          </Fragment>
        )}
      </Article>
    </Fragment>
  );
};

Location.propTypes = {
  history: object.isRequired,
  locationsCount: number,
  locationsError: string,
  locationsFetch: func.isRequired,
  locationsLoading: bool,
  locationsLookup: func.isRequired,
  match: shape({
    params: shape({
      slug: string,
    }),
  }).isRequired,
  prerenderReady: func.isRequired,
  targetDevice: string,
  trackOutboundLink: func,
};

const Article = styled.article`
  padding-bottom: ${rem("19px")};
  padding-top: ${rem("40px")};

  @media ${BREAKPOINT_WRAPPER} {
    padding-bottom: ${rem("56px")};
  }

  h1 {
    font-size: ${rem("28px")};
    margin-bottom: ${rem("22px")};
    line-height: 1.2;
  }

  h2 {
    font-size: ${rem("22px")};
    line-height: 1.2;
    text-transform: none;

    &.text-center {
      text-align: center;
    }
  }

  @media ${BREAKPOINT_WRAPPER} {
    h1 {
      font-size: ${rem("40px")};
      margin-bottom: ${rem("36px")};
      text-transform: uppercase;
    }

    h2 {
      font-size: ${rem("28px")};
      text-transform: uppercase;
    }
  }

  a {
    text-decoration: none;
    ${wordWrap("break-word")}

    &:not(.subtle) {
      color: ${colors.yellow};
    }

    &.subtle {
      color: inherit;
    }

    @media ${BREAKPOINT_WRAPPER} {
      &:not(.subtle) {
        color: ${colors.blue500};
      }
    }

    &:hover {
      text-decoration: underline;
    }

    &:focus {
      outline: ${rem("1px")} dotted;
      outline-offset: ${rem("2px")};
    }
  }
`;

const Content = styled.section`
  padding-left: ${rem("30px")};
  padding-right: ${rem("30px")};

  @media ${BREAKPOINT_WRAPPER} {
    padding-left: ${rem("60px")};
    padding-right: ${rem("60px")};
  }

  h2 {
    margin-bottom: ${rem("17px")};
    padding-top: ${rem("17px")};
  }

  p {
    line-height: 1.4;
    margin-bottom: ${rem("20px")};

    &.mb-4 {
      margin-bottom: ${rem("30px")};
    }

    &.lead {
      font-size: ${rem("22px")};
    }

    @media ${BREAKPOINT_WRAPPER} {
      &.md-offset {
        margin-bottom: ${rem("26px")};
      }
    }
  }
`;

const Row = styled.div`
  margin-bottom: ${rem("24px")};

  @media ${BREAKPOINT_WRAPPER} {
    display: flex;
    margin-bottom: ${rem("50px")};
  }
`;

const secondaryMargin = 40;
const secondaryWidth = 230;

const Secondary = styled.aside`
  margin-bottom: ${rem("26px")};

  h2,
  h3 {
    color: ${colors.gray600};
    font-size: ${rem("13px")};
    font-weight: ${fonts.weights.normal};
    line-height: 1.2;
    margin-bottom: ${rem("7px")};
    padding-top: 0;
    text-transform: uppercase;
  }

  h4 {
    padding-top: ${rem("6px")};
  }

  p {
    margin-bottom: ${rem("4px")};
  }

  @media ${BREAKPOINT_WRAPPER} {
    margin-bottom: 0;
    margin-right: ${rem(secondaryMargin + "px")};
    width: ${rem(secondaryWidth + "px")};

    h2,
    h3 {
      color: inherit;
      font-size: ${rem("18px")};
      font-weight: ${fonts.weights.bold};
      line-height: 1.2;
      margin-bottom: ${rem("4px")};
      text-transform: uppercase;
    }

    h4 {
      padding-top: ${rem("6px")};
    }

    p {
      margin-bottom: ${rem("10px")};
    }
  }
`;

const Primary = styled.div`
  @media ${BREAKPOINT_WRAPPER} {
    width: calc(100% - ${rem(secondaryWidth + secondaryMargin + "px")});
  }
`;

const LocationMap = styled.div`
  @media ${BREAKPOINT_WRAPPER} {
    margin-left: auto;
    max-width: 100%;
    width: ${rem("581px")};
  }
`;

const Banner = styled(FilestackImage)`
  display: block;
  margin-bottom: ${rem("30px")};
  max-width: 100%;

  > img {
    display: block;
    max-width: 100%;
  }

  @media ${BREAKPOINT_WRAPPER} {
    margin-bottom: ${rem("70px")};
  }
`;

const Description = styled(Raw)`
  margin-bottom: ${rem("46px")};

  &:before {
    background-color: ${colors.yellow};
    content: "";
    display: block;
    height: ${rem("4px")};
    margin-bottom: ${rem("29px")};
    margin-left: auto;
    margin-right: auto;
    width: ${rem("70px")};
  }

  > :last-child {
    margin-bottom: 0;
  }

  @media ${BREAKPOINT_WRAPPER} {
    margin-bottom: ${rem("60px")};
    margin-left: auto;
    margin-right: auto;
    max-width: ${rem("808px")};
    padding-left: ${rem("58px")};
    position: relative;

    &:before {
      bottom: ${rem("3px")};
      height: calc(100% - ${rem("9px")});
      left: 0;
      margin: 0;
      position: absolute;
      top: ${rem("6px")};
      width: ${rem("10px")};
    }
  }
`;

export default Location;
